import { format } from 'date-fns'

import { Badge } from '@/components/ui/badge'
import { entityHref } from '@/config/entityRoutes'
import { AppointmentStatusBadge } from '@/features/appointments/components/AppointmentStatusBadge'
import { formatDuration } from '@/features/calls/utils/callDetailFormat'
import { callReachPhrase } from '@/features/patients/utils/callReach'
import { formatShortDate } from '@/features/patients/utils/clientDisplay'
import {
  isAppointmentActive,
  splitAppointmentType,
} from '@/features/patients/utils/clientHistory'
import type { TimelineItem } from '@/features/patients/utils/clientTimeline'
import { labelStatusTone } from '@/lib/labelStatus'
import { cn, titleCase } from '@/lib/utils'

function joined(parts: (string | null | undefined)[]): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' · ')
}

export interface TimelineEntryContent {
  title: string
  when: string
  body: string | null
  tags: React.ReactNode
  href: string | null
  linkState?: unknown
}

/**
 * Post-call analysis writes these sentences in place of a missing summary or
 * transcript; they are no content, not content worth showing.
 */
const NO_KEY_POINTS = /^no key points available\b/i
const NO_TRANSCRIPT = /^transcript is empty\.?$/i

function callText(value: string | null | undefined, placeholder: RegExp) {
  const text = value?.trim()
  return text && !placeholder.test(text) ? text : null
}

export function timelineEntryContent(
  item: TimelineItem,
  now: Date,
  providerNames: ReadonlyMap<string, string>
): TimelineEntryContent {
  switch (item.kind) {
    case 'call': {
      const { call } = item
      return {
        title: `${call.type ? `${titleCase(call.type)} call` : 'Call'} · ${callReachPhrase(call)}`,
        when: joined([
          formatShortDate(item.date, now),
          call.duration ? formatDuration(call.duration) : null,
        ]),
        body: callText(call.key_points, NO_KEY_POINTS),
        tags: (
          <>
            {call.label ? (
              // Coloured by the label's own status (done / in review / retry),
              // as in the Calls table.
              <Badge
                className={cn(
                  'rounded-md border-transparent',
                  labelStatusTone(call.category).bg,
                  labelStatusTone(call.category).text
                )}>
                {call.label}
              </Badge>
            ) : null}
            {call.duty_name ? (
              <Badge variant="outline">{call.duty_name}</Badge>
            ) : null}
            {call.audio_url ? <Badge variant="outline">Recording</Badge> : null}
            {callText(call.transcript, NO_TRANSCRIPT) ? (
              <Badge variant="outline">Transcript</Badge>
            ) : null}
          </>
        ),
        href: entityHref({ type: 'call', id: call.call_id }),
      }
    }
    case 'duty': {
      const { duty } = item
      return {
        title: duty.duty_name,
        when: `assigned ${formatShortDate(item.date, now)}`,
        body: joined([
          duty.duty_attempt_number
            ? `Attempt ${duty.duty_attempt_number}`
            : null,
          duty.assignment_status,
        ]),
        tags: (
          <>
            <Badge variant={duty.is_done ? 'success' : 'secondary'}>
              {duty.is_done ? 'done' : 'in progress'}
            </Badge>
            {duty.retry_due && !duty.is_done ? (
              <Badge variant="warning">retry due</Badge>
            ) : null}
            {duty.duty_lifecycle_status === 'archived' ? (
              <Badge variant="outline">archived duty</Badge>
            ) : null}
          </>
        ),
        href: `/duties/${duty.duty_id}`,
        linkState: { dutyName: duty.duty_name },
      }
    }
    case 'visit': {
      const { visit } = item
      const provider = visit.provider_id
        ? providerNames.get(visit.provider_id)
        : null
      return {
        title: visit.visit_type?.trim() || 'Visit',
        when: formatShortDate(item.date, now),
        body: joined([provider, visit.location]) || null,
        tags: visit.program ? (
          <Badge variant="secondary">{visit.program}</Badge>
        ) : null,
        // The Visits page cannot open a single visit, so a visit links nowhere.
        href: null,
      }
    }
    case 'appointment': {
      const { appointment } = item
      const { code, description } = splitAppointmentType(
        appointment.appointment_type
      )
      // A cancelled or no-show appointment ahead is not "upcoming".
      const upcoming =
        item.date.getTime() >= now.getTime() && isAppointmentActive(appointment)
      return {
        title: description ?? code ?? 'Appointment',
        when: appointment.start_time
          ? `${formatShortDate(item.date, now)}, ${format(item.date, 'h:mm a')}`
          : formatShortDate(item.date, now),
        body: joined([appointment.provider_name, appointment.location]) || null,
        tags: (
          <>
            {upcoming ? <Badge variant="warning">upcoming</Badge> : null}
            <AppointmentStatusBadge
              status={appointment.status ?? appointment.source_status}
            />
            {appointment.source_name ? (
              <Badge variant="outline">{appointment.source_name}</Badge>
            ) : null}
          </>
        ),
        href: `/data/appointments?appointments.id=${appointment.id}&details=1`,
      }
    }
  }
}
